import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { VirtualizedTable, Skeleton, tableHelpers } from '@round/creatorbase-ui';
import { formatDate } from 'helpers';
import { ActionsCell } from './components/ActionsCell/ActionsCell';
import { CreatedByCell } from './components/CreatedByCell/CreatedByCell';
import { CreatorPoolsEmptyState } from './components/CreatorPoolsEmptyState/CreatorPoolsEmptyState';
import { getMeta, PoolRow, Meta, Column, PAGE_SIZE } from './helpers';
import styles from './CreatorPoolsTable.module.css';

const SkeletonCell = tableHelpers.SkeletonCell;
const TruncatedCell = tableHelpers.TruncatedCell;

type Props = {
    rows: PoolRow[];
    meta: Meta;
    fetchNextPage: () => void;
    onDeletePool: (pool: PoolRow) => void;
};

export const CreatorPoolsTable = ({ rows, meta, fetchNextPage, onDeletePool }: Props) => {
    const navigate = useNavigate();

    const columns: Column[] = useMemo(
        () => [
            {
                header: 'Name',
                id: 'name',
                accessorFn: (row) => row.name,
                cell: TruncatedCell,
                size: 220,
            },
            {
                header: 'Description',
                id: 'description',
                accessorFn: (row) => row.description || '-',
                cell: TruncatedCell,
                meta: { className: styles.descriptionCell },
                size: 240,
            },
            {
                header: 'Created by',
                id: 'createdBy',
                cell: CreatedByCell,
                size: 160,
            },
            {
                header: 'Last edited',
                id: 'last_edited_at',
                accessorFn: (row) => (row.last_edited_at ? formatDate(row.last_edited_at) : '-'),
                cell: SkeletonCell,
                size: 140,
            },
            {
                header: '',
                id: 'actions',
                meta: { className: styles.actionsCell },
                cell: ({ row: { original }, table }) => {
                    const { isLoading } = getMeta(table);

                    if (tableHelpers.isLoadingRow(original) || isLoading) {
                        return <Skeleton className={styles.actionsSkeleton} />;
                    }

                    return <ActionsCell onDelete={() => onDeletePool(original)} />;
                },
                size: 56,
            },
        ],
        [onDeletePool]
    );

    const data = tableHelpers.getLoadingRows(rows, { pageSize: PAGE_SIZE, isLoading: meta.isLoading });

    return (
        <VirtualizedTable
            columns={columns}
            data={data}
            meta={meta}
            virtualizerOptions={{
                estimateSize: () => 50,
                getItemKey: (index) => data[index]?.id ?? index,
            }}
            onEndReached={fetchNextPage}
            containerClassName={styles.tableContainer}
            onRowClick={(row) => {
                if (!tableHelpers.isLoadingRow(row.original)) {
                    navigate(`${row.original.id}/`);
                }
            }}
            noDataLabel={<CreatorPoolsEmptyState hasError={meta.hasError} />}
        />
    );
};
